import { useEffect, useMemo, useState } from 'react';
import { CheckSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTasks,
  useTaskSections,
  useUpdateTask,
  useCreateTask,
  TASK_FILTERS,
  type Task,
} from '../../hooks/queries/useTasks';
import { mergeDueDate } from '../../lib/tasks/taskReschedule';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { useTasksAssignments, useActiveCollaborators } from '../../hooks/queries/useTaskCollaboration';
import { useTaskTags, useTaskTagsFor } from '../../hooks/queries/useTaskTags';
import { useIsAdmin } from '../../hooks/usePermissions';
import { useTasksShortcuts } from '../../hooks/useTasksShortcuts';
import { useTasksStore } from '../../store/tasksStore';
import { useEffectiveTaskView } from '../../hooks/useEffectiveTaskView';
import { filterTaskIdsByTags, pruneTagFilter } from '../../lib/tasks/taskTagFilter';
import { matchesTaskSearch } from '../../lib/tasks/taskSearch';
import { TasksHeader } from './TasksHeader';
import { TasksListView } from './TasksListView';
import { TasksBoardView } from './TasksBoardView';
import { TasksCalendarView } from './TasksCalendarView';
import { TaskEditModal } from './TaskEditModal';
import { TaskCreateModal } from './TaskCreateModal';
import { useTaskQuickCapture } from '../../hooks/queries/useCabinetSettings';
import { ArchivedTasksModal } from './ArchivedTasksModal';
import { TasksBulkBar } from './TasksBulkBar';
import { Card } from '../ui/Card';
import { TaskContextMenu, useTaskContextMenu } from './TaskContextMenu';

export function TasksPage() {
  const { data: quickCapture = false } = useTaskQuickCapture();
  const [createOpen, setCreateOpen] = useState(false);
  useTasksShortcuts(() => setCreateOpen(true));
  const { data: dossiers = [], isLoading: isLoadingDossiers } = useDossiers();
  const activeDossierId = useTasksStore((s) => s.activeDossierId);
  const setActiveDossierId = useTasksStore((s) => s.setActiveDossierId);
  const setViewMode = useTasksStore((s) => s.setViewMode);
  const { viewMode, enabled } = useEffectiveTaskView();
  const tagFilterIds = useTasksStore((s) => s.tagFilterIds);
  const setTagFilterIds = useTasksStore((s) => s.setTagFilterIds);
  const pendingDeleteIds = useTasksStore((s) => s.pendingDeleteIds);
  const selectedTaskIds = useTasksStore((s) => s.selectedTaskIds);
  const clearSelection = useTasksStore((s) => s.clearSelection);
  const showCompletedTasks = useTasksStore((s) => s.showCompletedTasks);
  const setShowCompletedTasks = useTasksStore((s) => s.setShowCompletedTasks);

  const activeDossier = useMemo(
    () => dossiers.find((d) => d.id === activeDossierId) ?? null,
    [dossiers, activeDossierId],
  );

  const dossiersById = useMemo(
    () => new Map(dossiers.map((d) => [d.id, d])),
    [dossiers],
  );
  const dossierLookup = useMemo(
    () => (id: string) => {
      const d = dossiersById.get(id);
      return d ? { id: d.id, number: d.number, title: d.title } : null;
    },
    [dossiersById],
  );

  const baseFilter = viewMode === 'list' || showCompletedTasks
    ? TASK_FILTERS.ALL
    : TASK_FILTERS.ACTIVE_ONLY;
  const { data: allTasks = [], isLoading: isLoadingTasks } = useTasks(
    activeDossierId ? { ...baseFilter, dossier_id: activeDossierId } : baseFilter,
  );
  const { data: sections = [] } = useTaskSections(activeDossierId);

  // Filtre « par utilisateur » — réservé à l'admin (les autres ne voient déjà
  // que leurs propres tâches via la RLS). Filtre sur les assignés.
  const isAdmin = useIsAdmin();
  const { data: collaborators = [] } = useActiveCollaborators();
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const taskIds = useMemo(() => allTasks.map((t) => t.id), [allTasks]);
  const { data: assignmentsMap } = useTasksAssignments(
    isAdmin && assigneeFilter ? taskIds : [],
  );

  // Filtre par tags — ouvert à tous les rôles (filtre d'affichage).
  const { data: allTags = [] } = useTaskTags();
  const { data: tagMap = {}, isFetching: isTagMapFetching } = useTaskTagsFor(
    tagFilterIds.length > 0 ? taskIds : [],
  );

  // Un tag supprimé depuis les Paramètres laisserait un filtre fantôme qui
  // masque tout sans explication : on purge dès que la liste est connue.
  useEffect(() => {
    if (tagFilterIds.length === 0 || allTags.length === 0) return;
    const pruned = pruneTagFilter(
      tagFilterIds,
      allTags.map((t) => t.id),
    );
    if (pruned.length !== tagFilterIds.length) setTagFilterIds(pruned);
  }, [allTags, tagFilterIds, setTagFilterIds]);

  const tasks = useMemo(() => {
    let result = allTasks;
    if (searchQuery.trim()) {
      result = result.filter((t) => matchesTaskSearch(t, searchQuery));
    }
    if (viewMode !== 'list' && isAdmin && assigneeFilter) {
      result = result.filter((t) =>
        (assignmentsMap?.get(t.id) ?? []).some((a) => a.id === assigneeFilter),
      );
    }
    // Tant que tagMap est en cours de chargement (première activation du
    // filtre, cache froid), ne pas filtrer : sinon tout est vu comme « sans
    // tag » (contrat de filterTaskIdsByTags) et la liste flashe à vide.
    if (tagFilterIds.length > 0 && !isTagMapFetching) {
      const keep = filterTaskIdsByTags(
        result.map((t) => t.id),
        tagMap,
        tagFilterIds,
      );
      result = result.filter((t) => keep.has(t.id));
    }
    return result;
  }, [
    allTasks,
    searchQuery,
    isAdmin,
    assigneeFilter,
    assignmentsMap,
    tagFilterIds,
    tagMap,
    isTagMapFetching,
    viewMode,
  ]);

  // Exclut les taches en suppression differee : sans ce filtre, une tache
  // supprimee via sa corbeille resterait comptee dans la barre et recevrait
  // les actions groupees pendant sa fenetre d'annulation.
  const selectedTasks = useMemo(
    () => tasks.filter((t) => selectedTaskIds.has(t.id) && !pendingDeleteIds.has(t.id)),
    [tasks, selectedTaskIds, pendingDeleteIds],
  );

  // La sélection multiple ne doit pas survivre à un changement de contexte
  // (autre dossier/vue/recherche/filtre) : les ids sélectionnés perdraient
  // leur sens visuel.
  useEffect(() => {
    clearSelection();
  }, [viewMode, activeDossierId, searchQuery, tagFilterIds, assigneeFilter, clearSelection]);

  // Task edit modal — l'identifiant suffit, on récupère la version fresh depuis tasks
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const taskMenu = useTaskContextMenu();
  const editingTask = useMemo(
    () => (editingTaskId ? tasks.find((t) => t.id === editingTaskId) ?? null : null),
    [editingTaskId, tasks],
  );

  // Modal archives
  const [archivesOpen, setArchivesOpen] = useState(false);

  function handleOpenTask(task: Task) {
    setEditingTaskId(task.id);
  }

  const updateTask = useUpdateTask();
  function handleReschedule(task: Task, isoDay: string) {
    updateTask.mutate({ taskId: task.id, updates: mergeDueDate(task, isoDay) });
  }

  const createTaskMutation = useCreateTask();
  function handleQuickCreate(isoDay: string, title: string) {
    createTaskMutation.mutate(
      {
        content: title,
        priority: 3,
        due_date: isoDay,
        dossier_id: activeDossierId,
        section_id: null,
      },
      {
        onError: (err) => {
          toast.error(`Impossible de créer la tâche : ${err.message}`);
        },
      },
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto px-8 pt-6 pb-8">
        {/* Loading dossiers */}
        {isLoadingDossiers && (
          <div className="flex items-center justify-center py-16 text-cime-text-muted">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Chargement…
          </div>
        )}

        {/* Aucun dossier */}
        {!isLoadingDossiers && dossiers.length === 0 && (
          <EmptyDossiers />
        )}

        {/* Avec dossiers */}
        {!isLoadingDossiers && dossiers.length > 0 && (
          <>
            <TasksHeader
              dossiers={dossiers}
              activeDossier={activeDossier}
              onSelectDossier={setActiveDossierId}
              viewMode={viewMode}
              onChangeViewMode={setViewMode}
              enabledModes={enabled}
              onOpenArchives={() => setArchivesOpen(true)}
              showCompleted={showCompletedTasks}
              onToggleShowCompleted={
                viewMode === 'list' ? undefined : () => setShowCompletedTasks(!showCompletedTasks)
              }
              assigneeFilterOptions={viewMode === 'list' ? undefined : (isAdmin ? collaborators : undefined)}
              assigneeFilter={assigneeFilter}
              onChangeAssigneeFilter={setAssigneeFilter}
              tagOptions={allTags}
              tagFilterIds={tagFilterIds}
              onChangeTagFilter={setTagFilterIds}
              searchQuery={searchQuery}
              onChangeSearchQuery={setSearchQuery}
              onCreateTask={quickCapture ? undefined : () => setCreateOpen(true)}
            />

            {isLoadingTasks ? (
              <div className="flex items-center justify-center py-16 text-cime-text-muted">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Chargement des tâches…
              </div>
            ) : (
              <div className="contents" onContextMenu={(e) => taskMenu.openFrom(e, tasks)}>
                {viewMode === 'list' && (
                  <TasksListView
                    tasks={tasks}
                    sections={sections}
                    dossierId={activeDossierId}
                    onOpenTask={handleOpenTask}
                    dossierLookup={dossierLookup}
                    showDossier={activeDossierId === null}
                    hideComposer={!quickCapture}
                  />
                )}
                {viewMode === 'board' && (
                  <TasksBoardView
                    tasks={tasks}
                    sections={sections}
                    dossierId={activeDossierId}
                    onOpenTask={handleOpenTask}
                    hideComposer={!quickCapture}
                  />
                )}
                {viewMode === 'calendar' && (
                  <Card flush>
                    <div className="overflow-x-auto">
                      <TasksCalendarView
                        tasks={tasks.filter((t) => !pendingDeleteIds.has(t.id))}
                        onOpenTask={handleOpenTask}
                        onReschedule={handleReschedule}
                        onQuickCreate={handleQuickCreate}
                      />
                    </div>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {taskMenu.target ? (
        <TaskContextMenu target={taskMenu.target} onClose={taskMenu.close} onOpenTask={handleOpenTask} />
      ) : null}

      <TaskEditModal
        task={editingTask}
        sections={sections}
        onClose={() => setEditingTaskId(null)}
      />

      <TaskCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        dossierId={activeDossierId}
      />

      <ArchivedTasksModal
        open={archivesOpen}
        onClose={() => setArchivesOpen(false)}
        dossierId={activeDossierId}
        sections={sections}
      />

      {viewMode !== 'calendar' && selectedTasks.length > 0 && (
        <TasksBulkBar
          selectedTasks={selectedTasks}
          activeDossierId={activeDossierId}
          onClear={clearSelection}
        />
      )}
    </div>
  );
}

function EmptyDossiers() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 max-w-md mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-cime-primary/10 flex items-center justify-center mb-4">
        <CheckSquare className="w-8 h-8 text-cime-primary" />
      </div>
      <h2 className="text-base font-semibold text-cime-text-primary mb-1">
        Aucun dossier pour le moment
      </h2>
      <p className="text-sm text-cime-text-muted">
        Les tâches sont rattachées à un dossier juridique. Créez un premier dossier (depuis la
        future page Dossiers) pour commencer à ajouter des tâches.
      </p>
    </div>
  );
}
